import type { ApiError, ApiSuccess } from "@mmm/contracts";
import { ApiRequestError } from "@mmm/contracts";
import { reportReachable, reportUnreachable } from "./connectivity.js";
import { filenameFromDisposition } from "./contentDisposition.js";
import { getImpersonation } from "../auth/impersonation.js";
import { t } from "@mmm/ui";
/** Must match apps/api/src/auth/superadmin.ts. */
const IMPERSONATE_USER_HEADER = "X-Impersonate-User-Id";
const IMPERSONATE_WRITE_HEADER = "X-Impersonate-Write";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8787";
const DOCUMENTS_BASE_URL = import.meta.env.VITE_DOCUMENTS_BASE_URL ?? API_BASE_URL;

interface AccessTokenRequest {
  forceRefresh?: boolean;
}

type AccessTokenProvider = (request?: AccessTokenRequest) => Promise<string | null>;

let accessTokenProvider: AccessTokenProvider = async () => null;
// True only when the mounted auth provider is the Entra one (see
// AuthProvider.tsx `setAuthRequired`) — dev mode's getAccessToken() also
// resolves null, but that's by design (no auth required) and must keep
// sending requests unauthenticated, not throw AuthRequiredError.
let authRequired = false;

export class ApiTransportError extends Error {
  status?: number;
  body?: string;

  constructor(message: string, status?: number, body?: string) {
    super(message);
    this.name = "ApiTransportError";
    this.status = status;
    this.body = body;
  }
}

/** Thrown instead of sending a request when auth is required and no token
 * could be obtained without an interactive redirect (e.g. a transient
 * silent-refresh failure). Extends ApiTransportError so it falls into the
 * same "retriable" bucket useQuery.ts already applies to transport failures
 * — reads quietly keep retrying (and succeed once the user signs back in),
 * while commands.ts's run() special-cases it for a distinct toast. */
export class AuthRequiredError extends ApiTransportError {
  // Default evaluated at construction time (never at module load), so the
  // catalog is available and the message follows the current UI language.
  constructor(message = t("errors.sessionExpiredSignInAgain")) {
    super(message);
    this.name = "AuthRequiredError";
  }
}

export function setAccessTokenProvider(provider: AccessTokenProvider): () => void {
  accessTokenProvider = provider;
  return () => {
    if (accessTokenProvider === provider) accessTokenProvider = async () => null;
  };
}

/** See `authRequired` above — call with `true` from the Entra auth provider,
 * `false` from the dev auth provider. */
export function setAuthRequired(required: boolean): void {
  authRequired = required;
}

async function handleResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : undefined;
  } catch {
    body = undefined;
  }

  if (!response.ok) {
    if (isApiError(body)) throw new ApiRequestError(body);
    // Client-authored (unlike ApiRequestError's server-authored message) and
    // can surface to the user via main.tsx's unhandled-rejection toast — so
    // it comes from the catalog.
    throw new ApiTransportError(t("errors.apiRequestFailed", { status: response.status }), response.status, text);
  }

  if (!isApiSuccess<T>(body)) {
    throw new ApiTransportError(t("errors.apiInvalidResponse"), response.status, text);
  }
  return body.data;
}

async function authHeaders(base?: HeadersInit, request?: AccessTokenRequest): Promise<{ headers: Headers; token: string | null }> {
  const headers = new Headers(base);
  const token = await accessTokenProvider(request);
  if (token) headers.set("Authorization", `Bearer ${token}`);

  // Impersonation rides alongside the bearer token rather than replacing it:
  // the API always authenticates the real user first, then decides whether
  // they may act as the target. Sending these when not entitled just yields a
  // 403, so there's no need to gate them client-side.
  const impersonation = getImpersonation();
  if (impersonation.targetUserId) {
    headers.set(IMPERSONATE_USER_HEADER, impersonation.targetUserId);
    if (impersonation.write) headers.set(IMPERSONATE_WRITE_HEADER, "true");
  }

  return { headers, token };
}

async function fetchWithAuth(input: string, init: RequestInit = {}): Promise<Response> {
  const { headers, token } = await authHeaders(init.headers);
  if (!token && authRequired) {
    // Don't send an unauthenticated request that would just come back as an
    // opaque 401 — getAccessToken() already tried (and either has no
    // account, or hit a transient InteractionRequiredAuthError without
    // starting a redirect — see AuthProvider.tsx).
    throw new AuthRequiredError();
  }
  const response = await fetch(input, { ...init, headers });
  if (response.status !== 401 || !token) return response;

  const refreshed = await authHeaders(init.headers, { forceRefresh: true });
  if (!refreshed.token || refreshed.token === token) return response;
  return fetch(input, { ...init, headers: refreshed.headers });
}

/**
 * Whether an error thrown while making a request means the backend was
 * *unreachable* (feeds the connectivity banner via api/connectivity.ts), as
 * opposed to the server responding with a handled error.
 *
 * - AuthRequiredError: a local token problem, not a backend outage — reachable.
 * - ApiTransportError with no status: fetch never completed (raw network
 *   failure); a >=500 status is the server (or its Azure Functions wrapper,
 *   e.g. the bare `API_STARTUP_FAILED` 500) failing to serve the request.
 * - ApiRequestError code "unexpected": the API's own 500 envelope.
 * - Any other Error: fetch() rejects with a bare TypeError when the network is
 *   unreachable, before any typed error is constructed.
 * Everything else (4xx: validation, rateLimited, auth) proves the server DID
 * respond, so it counts as reachable.
 */
function isConnectivityFailure(error: unknown): boolean {
  if (error instanceof AuthRequiredError) return false;
  if (error instanceof ApiTransportError) return error.status === undefined || error.status >= 500;
  if (error instanceof ApiRequestError) return error.code === "unexpected";
  return error instanceof Error;
}

/** Runs a request and reports its outcome to the connectivity monitor:
 * reachable on success or any server response, unreachable only for the
 * failures classified above. Always re-throws so callers' handling is
 * unchanged. */
async function withConnectivityReport<T>(run: () => Promise<T>): Promise<T> {
  try {
    const result = await run();
    reportReachable();
    return result;
  } catch (error) {
    if (isConnectivityFailure(error)) reportUnreachable();
    else reportReachable();
    throw error;
  }
}

export async function postCommand<T>(commandName: string, input: unknown): Promise<T> {
  return withConnectivityReport(async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/commands/${commandName}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input ?? {}),
    });
    return handleResponse<T>(response);
  });
}

export async function getQuery<T>(queryName: string, params: Record<string, unknown> = {}): Promise<T> {
  return withConnectivityReport(async () => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) search.set(key, String(value));
    }
    const query = search.toString();
    const response = await fetchWithAuth(`${API_BASE_URL}/api/queries/${queryName}${query ? `?${query}` : ""}`);
    return handleResponse<T>(response);
  });
}

export async function checkBackendReady(signal?: AbortSignal): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/ready`, {
    headers: { Accept: "application/json" },
    signal,
  });
  if (!response.ok) {
    throw new ApiTransportError(`Backend is not ready yet (HTTP ${response.status}).`, response.status, await response.text());
  }
}

export async function getFile(queryName: string, params: Record<string, unknown> = {}): Promise<FileResponse> {
  return withConnectivityReport(async () => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) search.set(key, String(value));
    }
    const response = await fetchWithAuth(`${DOCUMENTS_BASE_URL}/api/files/${queryName}?${search.toString()}`);
    if (!response.ok) return handleResponse<never>(response);

    return {
      blob: await response.blob(),
      filename: filenameFromDisposition(response.headers.get("Content-Disposition")) ?? `${queryName}.bin`,
    };
  });
}

export interface FileResponse {
  blob: Blob;
  filename: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isApiError(value: unknown): value is ApiError {
  if (!isRecord(value) || !isRecord(value.error)) return false;
  return typeof value.error.code === "string" && typeof value.error.message === "string";
}

function isApiSuccess<T>(value: unknown): value is ApiSuccess<T> {
  return isRecord(value) && "data" in value && typeof value.requestId === "string";
}
